import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import { toast } from 'chameleon/ui-stack/ui/toast';

const MAX_INDIVIDUAL_FILE_SIZE = 25 * 1024 * 1024;
const MAX_BULK_FILE_SIZE = 25 * 1024 * 1024;
const MAX_FILE_COUNT = 10;

const UploadFilesControl = forwardRef((props, ref) => {
  const { label, labelClassName } = props;
  const fileUploadCtrl = useRef();
  const [files, setFiles] = useState([]);

  useImperativeHandle(ref, () => ({
    getUploadedFiles: () => {
      return files;
    },
    clearFiles: () => {
      setFiles([]);
    },
  }));

  /**
   * @todo - to be refactored - fix the linter issue
   * */
  const bytesToSize = useCallback((bytes) => {
    let sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return `${Math.round(bytes / 1024 ** i)} ${sizes[i]}`;
  }, []);

  const fileUploadChanged = useCallback(
    (e) => {
      let invalidFiles = [];
      const newFiles = [...files];
      let totalSize = newFiles.reduce(
        (cummulativeSize, file) => cummulativeSize + file.size,
        0,
      );

      // eslint-disable-next-line no-unused-vars
      for (let file of e.target.files) {
        let fileSize = file.size;

        if (fileSize + totalSize <= MAX_BULK_FILE_SIZE) {
          if (fileSize <= MAX_INDIVIDUAL_FILE_SIZE) {
            if (newFiles.length === MAX_FILE_COUNT) {
              toast.error(`You can only upload up to ${MAX_FILE_COUNT} files`, {
                timeout: 5,
              });
              break;
            }
            newFiles.push(file);
            totalSize += fileSize;
          } else {
            invalidFiles.push(file.name);
          }
        } else {
          toast.error(
            `Total maximum file upload size is ${bytesToSize(
              MAX_BULK_FILE_SIZE,
            )}`,
            { timeout: 5 },
          );
          break;
        }
      }

      if (invalidFiles.length > 0) {
        toast.error(
          `The following file(s) are too large: ${invalidFiles.join(', ')}`,
        );
        toast.error(
          `Files should not be larger than ${bytesToSize(
            MAX_INDIVIDUAL_FILE_SIZE,
          )} each`,
        );
      }

      setFiles(newFiles);
    },
    [files],
  );

  const uploadFiles = useCallback(() => {
    fileUploadCtrl.current.click();
  }, [fileUploadCtrl]);

  const removeFile = useCallback(
    (file) => {
      setFiles(files.filter((e) => e !== file));
    },
    [files],
  );

  const renderUploadedFiles = useCallback(() => {
    if (files.length < 1)
      return (
        <div className="text-14 text-gray-600 mx-4">
          <span>No file chosen</span>
        </div>
      );

    return files.map((file, index) => (
      <div
        className="flex justify-between items-center auto-cols-max items-center gap-x-3 h-9.8 mx-4"
        key={index}
      >
        <span className="text-gray-900 text-12">{file.name}</span>
        <ButtonWithIcon
          size="custom"
          iconWidth="12"
          iconHeight="12"
          isNaked
          isIcon
          icon="close"
          onClick={() => removeFile(file)}
        />
      </div>
    ));
  }, [files]);

  return (
    <div className="grid gap-y-2 mx-2">
      <input
        type="file"
        hidden
        multiple
        ref={fileUploadCtrl}
        onChange={fileUploadChanged}
        aria-label={label}
      />
      <label className={`text-14 font-medium ${labelClassName}`}>{label}</label>
      <div>
        <ButtonWithIcon size="small" icon="upload" onClick={uploadFiles}>
          Choose file(s)
        </ButtonWithIcon>
      </div>
      <div className="grid grid-flow-row-dense grid-cols-3 grid-rows-1 gap-4">
        {renderUploadedFiles()}
      </div>
    </div>
  );
});

export default UploadFilesControl;
